import type { RouteObject } from 'react-router'
import type { JobsFeature } from '@/features/jobs'
import type { WaitForAuthResolutionFn } from '@/features/iam'
import {
  createCheckoutViewLoader,
  createDashboardViewLoader,
  createDraftJobViewLoader,
  createFirmDashboardViewLoader,
  createJobDetailsViewLoader,
  createJobListViewLoader,
  createScopingJobsViewLoader,
} from '@/features/jobs/job-route-loaders'
import { links } from '@/utils/link'

/**
 * Options for {@link createRoutes}.
 */
export interface CreateJobRoutesOptions {
  jobs: JobsFeature
  waitForAuthResolution: WaitForAuthResolutionFn
}

/**
 * Creates the jobs routes that render inside the shell.
 */
export function createRoutes(options: CreateJobRoutesOptions): RouteObject[] {
  return [
    {
      index: true,
      loader: createDashboardViewLoader(options),
      Component: null,
    },
    {
      path: '/new/:jobId?',
      loader: createDraftJobViewLoader(options),
      lazy: {
        Component: () =>
          import('@/features/jobs/drafting/components/JobCreation/JobCreationContainer').then(
            (r) => r.default,
          ),
      },
    },
    {
      path: '/jobs/*',
      loader: createJobListViewLoader(options),
      lazy: {
        Component: () =>
          import('@/features/jobs/list/components/JobManagement/JobManagementContainer').then(
            (r) => r.default,
          ),
      },
    },
    {
      path: '/jobs/:jobId',
      loader: createJobDetailsViewLoader(options),
      lazy: {
        Component: () =>
          import('@/features/jobs/details/components/JobRoot/JobRootContainer').then(
            (r) => r.default,
          ),
        ErrorBoundary: () =>
          import('@/features/jobs/details/components/JobRoot/JobRootErrorBoundaryContainer').then(
            (r) => r.default,
          ),
      },
    },
    {
      path: '/jobs/:jobId/checkout',
      loader: createCheckoutViewLoader(options),
      lazy: {
        Component: () =>
          import('@/features/jobs/drafting/checkout/components/Checkout/CheckoutContainer').then(
            (r) => r.default,
          ),
        ErrorBoundary: () =>
          import('@/features/jobs/drafting/checkout/components/Checkout/CheckoutErrorBoundaryContainer').then(
            (r) => r.default,
          ),
      },
    },
    {
      path: '/checkout',
      loader: createCheckoutViewLoader(options),
      lazy: {
        Component: () =>
          import('@/features/jobs/drafting/checkout/components/Checkout/CheckoutContainer').then(
            (r) => r.default,
          ),
        ErrorBoundary: () =>
          import('@/features/jobs/drafting/checkout/components/Checkout/CheckoutErrorBoundaryContainer').then(
            (r) => r.default,
          ),
      },
    },
    {
      path: '/dashboard/*',
      loader: createFirmDashboardViewLoader(options),
      lazy: {
        Component: () =>
          import('@/features/jobs/dashboard/components/FirmDashboard/FirmDashboardContainer').then(
            (r) => r.default,
          ),
      },
    },
    {
      path: '/scoping-jobs/:scopeWorkId?/*',
      loader: createScopingJobsViewLoader(options),
      lazy: {
        Component: () =>
          import('@/features/jobs/scoping/components/OCRScopingJobs/OCRScopingJobsContainer').then(
            (r) => r.default,
          ),
      },
    },
  ]
}

/**
 * Creates the jobs routes that render without the shell.
 */
export function createMobileRoutes(
  options: CreateJobRoutesOptions,
): RouteObject[] {
  return [
    {
      path: '/mobile/jobs/:jobId',
      loader: createJobDetailsViewLoader(options, links.mobileJob),
      lazy: {
        Component: () =>
          import('@/features/jobs/details/components/MobileJob/MobileJobContainer').then(
            (r) => r.default,
          ),
        ErrorBoundary: () =>
          import('@/features/jobs/details/components/MobileJob/MobileJobErrorBoundaryContainer').then(
            (r) => r.default,
          ),
      },
    },
  ]
}
